import {
  DollarCircleOutlined,
  EyeFilled,
  FileTextOutlined,
  MessageFilled,
  MessageOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import Flag from 'react-flagpack';

import {
  Button,
  Checkbox,
  DockingBillingStatusTag,
  ErrorBoundary,
} from '../../../../../components/index';
import {
  DockingBillingStatusType,
  StopoverGroupbyDockingStatisticsType,
} from '../../../../../types';
import { createDateStringPtBr } from '../../../../../utils/utils';
import { BillingStatusFilter } from './BillingStatusFilter';

// Atracação com cobrança não arquivada é recusada com 409 pelo backend: bloquear aqui
// evita o erro e a notificação de falha que ele dispara.
function hasActiveCharge(chargeStatus?: string) {
  return !!chargeStatus && chargeStatus !== 'ARCHIVED';
}

// O backend recusa o fundeio com 409: o porto não cobra por ele, e a atracação
// nunca entra no ciclo de faturamento.
function isNotBillable(billingStatus?: string) {
  return billingStatus === 'NOT_BILLABLE';
}

function chargeTooltip(chargeStatus?: string, billingStatus?: string) {
  if (isNotBillable(billingStatus)) {
    return 'O porto não cobra pelo fundeio, então esta atracação não gera faturamento.';
  }
  // Os dois estados de transmissão caem em "tem cobrança ativa", e ali o conselho
  // de arquivar e gerar outra é o pior caminho: no erro de envio o certo é
  // reenviar, e arquivar duplicaria no RM a movimentação que já foi gravada.
  if (billingStatus === 'FINANCE_SEND_ERROR') {
    return 'O envio ao financeiro falhou. O reenvio é feito na tela de Faturamento, em "Gerar demonstrativo".';
  }
  if (billingStatus === 'PROCESSING_BY_FINANCE') {
    return 'O envio ao financeiro está em andamento. Aguarde o desfecho.';
  }
  return hasActiveCharge(chargeStatus)
    ? 'Esta atracação já possui um faturamento ativo. Arquive o faturamento existente para gerar um novo.'
    : 'Gerar faturamento';
}

// Sem código ANTAQ não há o que declarar no XML, e o fundeio pode chegar sem
// `docking_place`. O checkbox da linha e a seleção em massa precisam usar a
// mesma checagem, senão a linha desabilitada entra no lote pelo cabeçalho.
export function hasAntaqCode(record: {
  docking_place?: { antaq_code?: string | null } | null;
}) {
  return !!record.docking_place?.antaq_code;
}

function observationTooltip(hasObservation?: boolean) {
  return hasObservation
    ? 'Ver observações da atracação'
    : 'Adicionar observação à atracação';
}

export function columns(
  dockingsIdToEdit: number[],
  onSelectItem: (e: boolean, record: number) => void,
  onOpenDrawer: (docking: StopoverGroupbyDockingStatisticsType) => void,
  checkedAll: boolean,
  onCheckedAll: (value: boolean) => void,
  onCreateCharge: (docking_id: number) => void,
  chargingDockingId: number | null,
  selectedBillingStatus: DockingBillingStatusType[],
  onChangeBillingStatus: (status: DockingBillingStatusType[]) => void,
  onOpenObservations: (docking: StopoverGroupbyDockingStatisticsType) => void
): ColumnsType<Record<string, any>> {
  return [
    {
      title() {
        return (
          <Checkbox
            defaultChecked={checkedAll}
            checked={checkedAll}
            onChange={(e) => onCheckedAll(e.target.checked)}
          />
        );
      },
      width: '5%',
      dataIndex: 'is_selected',
      render(value, record) {
        return (
          <Checkbox
            defaultChecked={value}
            disabled={!hasAntaqCode(record)}
            checked={
              dockingsIdToEdit.find((id) => id === record.docking__id) !==
              undefined
            }
            onChange={(e) => onSelectItem(e.target.checked, record.docking__id)}
          />
        );
      },
    },
    {
      title: 'atracação',

      dataIndex: 'code',
      width: '10%',
      sorter: {
        compare: (a: any, b: any) => a.code?.localeCompare(b.code),
        multiple: 1,
      },
    },
    {
      title: 'embarcação',
      dataIndex: 'vessel__flag',
      width: '15%',
      sorter: {
        compare: (a: any, b: any) =>
          a.vessel__name?.localeCompare(b.vessel__name),
        multiple: 1,
      },
      render: (value, record) => {
        return (
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ width: '20px' }}>
              {value && (
                <ErrorBoundary replace="">
                  <Flag code={value} size="m" />
                </ErrorBoundary>
              )}
            </div>
            <span
              style={{
                textTransform: 'capitalize',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {record.vessel__name?.toLowerCase() || ''}
            </span>
          </div>
        );
      },
    },
    {
      title: 'finalidade',
      dataIndex: ['docking_goal', 'description'],
      width: '15%',
      sorter: {
        compare: (a: any, b: any) =>
          a.docking_goal?.description?.localeCompare(
            b.docking_goal?.description
          ),
        multiple: 1,
      },
    },
    {
      title: 'berço',
      dataIndex: ['docking_place', 'name'],
      width: '10%',
      sorter: {
        compare: (a: any, b: any) =>
          a.docking_place?.name?.localeCompare(b.docking_place?.name),
        multiple: 1,
      },
    },
    {
      title: 'desatracação',
      dataIndex: 'last_cable_untied_in',
      width: '13%',
      sorter: {
        compare: (a: any, b: any) =>
          a.last_cable_untied_in?.localeCompare(b.last_cable_untied_in),
        multiple: 1,
      },
      render: (value) => createDateStringPtBr(value),
    },
    {
      title() {
        return (
          <>
            status do faturamento
            <BillingStatusFilter
              selectedBillingStatus={selectedBillingStatus}
              onChangeBillingStatus={onChangeBillingStatus}
            />
          </>
        );
      },
      dataIndex: 'billing_status',
      width: '13%',
      render: (value, record) => (
        <DockingBillingStatusTag
          status={value}
          rmRecordKey={record.rm_record_key}
          rmErrorReason={record.rm_error_reason}
        />
      ),
    },
    {
      title: 'status xml',
      dataIndex: 'last_date_xml',
      width: '11%',
      sorter: {
        compare: (a: any, b: any) =>
          a.last_date_xml?.localeCompare(b.last_date_xml),
        multiple: 1,
      },
      render: (value) =>
        value ? (
          createDateStringPtBr(value)
        ) : (
          <div>
            <FileTextOutlined style={{ color: 'var(--neutral_medium)' }} />
            <i style={{ color: 'var(--neutral_medium)', marginLeft: '5px' }}>
              Ainda não gerado
            </i>
          </div>
        ),
    },
    {
      title: '',
      width: '9%',
      align: 'right' as const,
      render: (_, record) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              justifyContent: 'right',
            }}
          >
            <Button
              icon={<EyeFilled />}
              onClick={() =>
                onOpenDrawer(record as StopoverGroupbyDockingStatisticsType)
              }
            />
            {/* topRight ancora a borda direita do tooltip na do botão. No
                padrão "top" ele é centralizado e, sendo esta a última coluna,
                transborda a direita da página, criando barra de rolagem
                horizontal e reposicionando o layout inteiro no hover. */}
            <Tooltip
              placement="topRight"
              title={chargeTooltip(record.charge_status, record.billing_status)}
            >
              <Button
                icon={<DollarCircleOutlined />}
                disabled={
                  chargingDockingId !== null ||
                  hasActiveCharge(record.charge_status) ||
                  isNotBillable(record.billing_status)
                }
                loading={chargingDockingId === record.docking__id}
                onClick={() => onCreateCharge(record.docking__id)}
              />
            </Tooltip>
            <Tooltip
              placement="topRight"
              title={observationTooltip(record.has_observation)}
            >
              <Button
                icon={
                  record.has_observation ? (
                    <MessageFilled />
                  ) : (
                    <MessageOutlined />
                  )
                }
                onClick={() =>
                  onOpenObservations(
                    record as StopoverGroupbyDockingStatisticsType
                  )
                }
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];
}
